import { Component, ViewChild, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DocRefListComponent } from '../../components/doc-ref-list/doc-ref-list.component';
import { DocRefFormComponent } from '../../components/doc-ref-form/doc-ref-form.component';
import { DocumentTypeService } from '../../services/document-type.service';
import { CatalogService } from '@services/data/catalog.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';
import {
  saveDocumentTypeSwalOptions,
  saveDocumentTypeSuccessText
} from 'app/feature/management/process-document-management/utils/doc-requirement-audit.utils';
import Swal from 'sweetalert2';
import { NgbModal, NgbModalRef, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-document-type-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DocRefListComponent,
    DocRefFormComponent,
    PageHeaderComponent,
    MobileFooterComponent,
    NgbModalModule
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './document-type-management.component.html'
})
export class DocumentTypeManagementComponent {
  @ViewChild(DocRefListComponent) typeList!: DocRefListComponent;
  @ViewChild('typeModal') typeModal: any;

  private modalRef?: NgbModalRef;
  selectedType: any = null;
  documentTypeForm: FormGroup;
  mode = signal<FormMode>(FormMode.VIEW);
  formMode = FormMode;
  showDetail = false;

  public appSettings = inject(AppSettings);
  readonly perm = inject(PermissionService);
  private fb = inject(FormBuilder);
  private documentTypeService = inject(DocumentTypeService);
  private catalogService = inject(CatalogService);
  private modalService = inject(NgbModal);

  public Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2800,
    timerProgressBar: true
  });

  constructor() {
    this.documentTypeForm = this.fb.group({
      id: [null],
      slug: [null],
      name: [null, [Validators.required]],
      nameShort: [null],
      description: [null],
      order: [0],
      allowExpiration: [false],
      isActive: [true]
    });
  }

  get showMobileDetail(): boolean {
    return this.appSettings.isMobile() && this.showDetail;
  }

  onTypeSelected(row: any): void {
    this.openEditor(row, FormMode.EDIT);
  }

  private refreshAfterMutation(): void {
    this.typeList?.loadTypes();
    this.catalogService.reloadDocumentTypes();
  }

  private payloadFromForm() {
    const raw = this.documentTypeForm.getRawValue();
    return {
      slug: raw.slug ? String(raw.slug).trim().toUpperCase() : null,
      name: String(raw.name || '').trim(),
      nameShort: raw.nameShort ? String(raw.nameShort).trim() : null,
      description: raw.description ? String(raw.description).trim() : null,
      order: Number(raw.order) || 0,
      allowExpiration: !!raw.allowExpiration,
      isActive: !!raw.isActive
    };
  }

  async saveType(): Promise<void> {
    if (this.documentTypeForm.invalid) {
      this.documentTypeForm.markAllAsTouched();
      return;
    }
    const data = this.payloadFromForm();

    if (this.mode() === FormMode.EDIT) {
      const slug = this.selectedType?.slug;
      if (!slug) return;
      const expirationChanged =
        !!this.selectedType?.allowExpiration !== !!data.allowExpiration;
      let applyToOpen = false;
      if (expirationChanged) {
        const choice = await Swal.fire(saveDocumentTypeSwalOptions());
        if (choice.isDismissed) return;
        applyToOpen = choice.isDenied;
      }
      this.documentTypeService.update(slug, data, { applyToOpen }).subscribe({
        next: (res) => {
          if (expirationChanged) {
            Swal.fire({
              icon: 'success',
              title: 'Tipo actualizado',
              text: saveDocumentTypeSuccessText(applyToOpen, res?.affectedCount ?? 0),
              timer: 2800,
              showConfirmButton: false
            });
          } else {
            this.Toast.fire({ icon: 'success', title: 'Tipo actualizado' });
          }
          this.refreshAfterMutation();
          this.closeDetail();
        },
        error: (err) => this.Toast.fire({
          icon: 'error',
          title: err?.error?.message || 'No se pudo actualizar'
        })
      });
      return;
    }

    this.documentTypeService.create(data).subscribe({
      next: () => {
        this.Toast.fire({ icon: 'success', title: 'Tipo creado' });
        this.refreshAfterMutation();
        this.closeDetail();
      },
      error: (err) => this.Toast.fire({
        icon: 'error',
        title: err?.error?.message || 'No se pudo crear'
      })
    });
  }

  newType(): void {
    this.openEditor(null, FormMode.CREATE);
  }

  private openEditor(row: any | null, mode: FormMode): void {
    this.selectedType = row;
    this.mode.set(mode);
    if (mode === FormMode.CREATE) {
      this.documentTypeForm.reset({
        id: null,
        slug: null,
        name: null,
        nameShort: null,
        description: null,
        order: 0,
        allowExpiration: false,
        isActive: true
      });
      this.documentTypeForm.get('slug')?.enable();
    } else {
      this.documentTypeForm.patchValue({
        id: row.id,
        slug: row.slug,
        name: row.name,
        nameShort: row.nameShort ?? null,
        description: row.description ?? null,
        order: row.order ?? 0,
        allowExpiration: !!row.allowExpiration,
        isActive: row.isActive !== false
      });
      this.documentTypeForm.get('slug')?.disable();
    }
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (this.appSettings.isMobile()) {
      this.showDetail = true;
      return;
    }
    this.modalRef = this.modalService.open(this.typeModal, { size: 'lg' });
  }

  close(): void {
    this.modalRef?.close();
  }

  closeDetail(): void {
    this.showDetail = false;
    this.selectedType = null;
    this.mode.set(FormMode.VIEW);
    this.close();
  }
}
